/**
 * ProjectSelector — dropdown with search input and project rows.
 * Each row shows project name + template hint pill.
 * Includes "All Projects" option to enter aggregate mode.
 */

import { ChevronDown, Search } from 'lucide-react';
import { useState, useRef, useEffect, useMemo } from 'react';

import { cn } from '@/lib/utils';
import { useDashboardContext } from '@/pages/dashboard/DashboardContext';

// ============================================================================
// COMPONENT
// ============================================================================

export function ProjectSelector() {
  const {
    filters,
    filterMode,
    projects,
    projectsLoading,
    templates,
    selectProject,
    enterAggregateMode,
  } = useDashboardContext();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  // Autofocus search input when dropdown opens
  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open]);

  // Resolve template name from templateId
  const templateNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of templates) {
      map.set(t.templateId, t.name);
    }
    return map;
  }, [templates]);

  // Filter projects by search text
  const filteredProjects = useMemo(
    () => projects.filter((p) => p.name.toLowerCase().includes(search.toLowerCase())),
    [projects, search]
  );

  // Current display name
  const displayName = useMemo(() => {
    if (filterMode === 'aggregate') return 'All Projects';
    const current = projects.find((p) => p.id === filters.projectId);
    return current?.name ?? 'Select Project';
  }, [filterMode, filters.projectId, projects]);

  // Loading skeleton
  if (projectsLoading) {
    return <div className="animate-pulse h-8 w-32 rounded-lg bg-muted" />;
  }

  function handleSelectProject(projectId: string) {
    selectProject(projectId);
    setOpen(false);
    setSearch('');
  }

  function handleSelectAll() {
    enterAggregateMode();
    setOpen(false);
    setSearch('');
  }

  return (
    <div className="relative" ref={containerRef}>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium',
          'border border-border-subtle bg-surface hover:bg-surface-subtle transition-colors'
        )}
      >
        <span className="truncate max-w-[180px]">{displayName}</span>
        <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', open && 'rotate-180')} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 w-72 rounded-xl border border-border-subtle bg-surface shadow-lg">
          {/* Search input */}
          <div className="p-1">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-secondary/60" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search projects..."
                className="w-full pl-8 pr-3 py-2 text-sm border-b border-border-subtle bg-transparent placeholder:text-secondary/60 focus:outline-none"
              />
            </div>
          </div>

          {/* Project list */}
          <div className="max-h-60 overflow-y-auto py-1">
            {/* All Projects option */}
            <button
              onClick={handleSelectAll}
              className={cn(
                'w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-surface-subtle transition-colors cursor-pointer',
                filterMode === 'aggregate' && 'bg-brand/5'
              )}
            >
              <span className="font-medium text-primary">All Projects</span>
            </button>

            {/* Individual projects */}
            {filteredProjects.map((p) => {
              const templateName = p.templateId ? templateNameMap.get(p.templateId) : undefined;
              const isSelected = filterMode === 'single-project' && filters.projectId === p.id;

              return (
                <button
                  key={p.id}
                  onClick={() => handleSelectProject(p.id)}
                  className={cn(
                    'w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-surface-subtle transition-colors cursor-pointer',
                    isSelected && 'bg-brand/5'
                  )}
                >
                  <span className="font-medium text-primary truncate">{p.name}</span>
                  {templateName && (
                    <span className="text-[10px] bg-brand/10 text-brand rounded-full px-1.5 py-0.5 flex-shrink-0 ml-2">
                      {templateName}
                    </span>
                  )}
                </button>
              );
            })}

            {filteredProjects.length === 0 && search && (
              <div className="px-3 py-2 text-sm text-secondary">No projects found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
